import { type AiBuilderChatPlaceholder } from '../constants';
import {
  getActiveChoiceMessage,
  getActiveConfirmMessage,
} from '../store/aiBuilderMessageStream';
import { isAiBuilderUsageBlocked } from '../store/aiBuilderUsagePolicy';
import {
  AI_BUILDER_COMPOSER_MAX_MESSAGE_LENGTH,
  type AiBuilderMessage,
  type AiBuilderUsageInfo,
} from '../store/types';

import { type AiBuilderChatCopy } from './aiBuilderChatCopy';

export type AiBuilderComposerInteractionMode =
  | 'freeText'
  | 'softChoice'
  | 'closedAction';

export type AiBuilderComposerPolicyInput = {
  draft: string;
  hasSelectedFile: boolean;
  isGenerating: boolean;
  messages: AiBuilderMessage[];
  placeholder: AiBuilderChatPlaceholder;
  turnInProgress: boolean;
  usage: AiBuilderUsageInfo | null;
  copy: AiBuilderChatCopy;
};

export type AiBuilderComposerPolicy = {
  activeChoiceMessage: ReturnType<typeof getActiveChoiceMessage>;
  activeConfirmMessage: ReturnType<typeof getActiveConfirmMessage>;
  canEditDraft: boolean;
  canSubmit: boolean;
  canUseSourceAttachment: boolean;
  hasActiveTurn: boolean;
  interactionMode: AiBuilderComposerInteractionMode;
  isClosedActionMode: boolean;
  isDraftOverMessageLengthLimit: boolean;
  placeholder: string;
  remainingTurnsLabel: string | null;
  usageBlocked: boolean;
};

/**
 * Composer의 파생 상태 정책을 한 곳에서 계산한다.
 * - freeText: 자유 입력 + 파일 첨부
 * - softChoice: choice 메시지 활성 — 선택지 또는 자유 입력으로 제출
 * - closedAction: confirm 메시지 활성 — 입력 잠금, 액션 버튼으로만 진행
 */
export const deriveAiBuilderComposerPolicy = ({
  draft,
  hasSelectedFile,
  isGenerating,
  messages,
  placeholder,
  turnInProgress,
  usage,
  copy,
}: AiBuilderComposerPolicyInput): AiBuilderComposerPolicy => {
  const activeChoiceMessage = getActiveChoiceMessage(messages);
  const activeConfirmMessage = getActiveConfirmMessage(messages);
  const interactionMode = getAiBuilderComposerInteractionMode({
    hasActiveChoiceMessage: !!activeChoiceMessage,
    hasActiveConfirmMessage: !!activeConfirmMessage,
  });
  const isClosedActionMode = interactionMode === 'closedAction';
  const isDraftOverMessageLengthLimit =
    draft.length > AI_BUILDER_COMPOSER_MAX_MESSAGE_LENGTH;
  const usageBlocked = isAiBuilderUsageBlocked(usage);
  const hasDraftText = draft.trim().length > 0;
  const hasChoiceSelection =
    (activeChoiceMessage?.selectedOptionIds.length ?? 0) > 0;
  const canSubmitBase = !isDraftOverMessageLengthLimit && !usageBlocked;
  const canSubmit =
    interactionMode === 'softChoice'
      ? canSubmitBase && (hasDraftText || hasChoiceSelection)
      : interactionMode === 'freeText' &&
        canSubmitBase &&
        (hasDraftText || hasSelectedFile);
  const hasActiveTurn = isGenerating || turnInProgress;

  return {
    activeChoiceMessage,
    activeConfirmMessage,
    canEditDraft: !isClosedActionMode && !usageBlocked,
    canSubmit,
    canUseSourceAttachment: !usageBlocked && interactionMode === 'freeText',
    hasActiveTurn,
    interactionMode,
    isClosedActionMode,
    isDraftOverMessageLengthLimit,
    placeholder: getAiBuilderComposerPlaceholder({
      copy,
      interactionMode,
      placeholder,
    }),
    remainingTurnsLabel: getAiBuilderRemainingTurnsLabel(usage, copy),
    usageBlocked,
  };
};

const getAiBuilderComposerInteractionMode = ({
  hasActiveChoiceMessage,
  hasActiveConfirmMessage,
}: {
  hasActiveChoiceMessage: boolean;
  hasActiveConfirmMessage: boolean;
}): AiBuilderComposerInteractionMode => {
  if (hasActiveConfirmMessage) return 'closedAction';
  if (hasActiveChoiceMessage) return 'softChoice';
  return 'freeText';
};

const getAiBuilderComposerPlaceholder = ({
  copy,
  interactionMode,
  placeholder,
}: {
  copy: AiBuilderChatCopy;
  interactionMode: AiBuilderComposerInteractionMode;
  placeholder: AiBuilderChatPlaceholder;
}) => {
  switch (interactionMode) {
    case 'softChoice':
      return copy.placeholder.softChoice;
    case 'closedAction':
      return copy.placeholder.closedAction;
    case 'freeText':
    default:
      return copy.placeholder[placeholder];
  }
};

const getAiBuilderRemainingTurnsLabel = (
  usage: AiBuilderUsageInfo | null,
  copy: AiBuilderChatCopy,
): string | null => {
  if (!usage || usage.limit < 0 || usage.remaining < 0) return null;

  return copy.usage.remainingTurns(usage.remaining);
};
